<template>
  <div class="p-2 sm:p-3">
    <div class="flex flex-wrap justify-center">
      <!-- Menu render section -->
      <div class="min-w-full sm:min-w-30 max-w-2xl m-2 flex-1">
        <div class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12">
          <h1
              v-if="menuExists"
              class="font-semibold text-2xl lg:text-3xl text-center mb-8"
          >
            Pick items to include in your order menu
          </h1>
          <h1
              v-else
              class="font-semibold text-2xl lg:text-3xl text-center mb-8"
          >
            Menu is not ready
          </h1>

          <section class="mt-5" style="min-height: 100px">
            <div v-if="progressLoading">
              <Spinner class="mx-auto" orange/>
            </div>
            <div v-else>
              <!-- if menu available -->
              <template v-if="menuExists">
                <div
                    v-for="(category, index) in categories"
                    :key="category.id"
                    class="mb-12"
                >
                  <header class="mb-2 flex justify-between items-center">
                    <div>
                      <h1
                          class="text-xl lg:text-2xl text-primary font-extrabold text-left py-2"
                      >
                        {{ category.title }}
                      </h1>
                      <span class="text-left">{{
                          trimString(category.name, 50)
                        }}</span>
                      <p class="text-sm opacity-75">
                        {{ category.description }}
                      </p>
                    </div>
                  </header>

                  <div
                      v-show="index === 0"
                      class="flex items-center justify-end pr-2"
                  >
                    <span class="text-grey-500 font-light text-xs mr-2"
                    >IS DEFAULT?</span
                    >
                    <v-popover disabled placement="bottom-end" trigger="hover">
                      <button class="btn" type="button">
                        <img
                            alt="BuildaBox Order Process"
                            src="@/assets/icons/ic_info.png"
                        />
                      </button>
                      <div slot="popover">
                        <div class="w-64 p-4">
                          The default item
                        </div>
                      </div>
                    </v-popover>
                  </div>
                  <div>
                    <div v-for="item in category.items" :key="item.id">
                      <div class="mb-6">
                        <div
                            class="flex items-center justify-between md:items-start"
                        >
                          <div
                              class="w-20 h-20 rounded overflow-hidden flex items-center justify-center flex-shrink-0"
                          >
                            <img
                                v-if="item.image"
                                :src="item.image"
                                alt="BuildaBox Order Process"
                            />
                          </div>
                          <div class="mx-5 flex-grow hidden md:block">
                            <div class="font-base font-semibold mb-1">
                              {{ item.name }}
                            </div>
                            <div class="text-sm opacity-75">
                              {{ item.description }}
                            </div>


                            <ManualItemAmount v-if="item.allow_bulk" :category="category.id"
                                              :item="item.id" @manual-selected="manualSelected"
                                              @remove-manual-selected="removeManualSelected"/>
                          </div>
                          <div class="w-20 flex-shrink-0 flex justify-between">
                            <CustomRadioButton
                                v-model="category.default_item"
                                :name="category.name"
                                :value="item.id"
                                @change="onChangeDefaultItem(category)"
                            ></CustomRadioButton>
                            <CustomCheckBox
                                v-model="item.isSelected"
                                :disabled="item.click_disabled"
                                @change="onSelectItem(category)"
                            ></CustomCheckBox>
                          </div>
                        </div>
                        <div class="md:hidden py-5">
                          <div class="font-base font-semibold mb-1">
                            {{ item.name }}
                          </div>
                          <div class="text-sm opacity-75">
                            {{ item.description }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- no menu message -->
              <template v-else>
                <div>
                  Once the caterer you have invited has added the available menu
                  items you will be able to choose the selections sent to your
                  attendees here.
                </div>
              </template>
            </div>
          </section>


          <section class="mt-20">
            <div class="flex flex-col-reverse sm:flex-row justify-between">
              <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mt-5 sm:mt-0"
                  @click="$emit('back')"
              >
                <span>Back</span>
              </button>
              <button
                  :disabled="!canSubmit"
                  class="btn btn-primary font-semibold rounded-lg px-10 min-w-10"
                  @click="submit"
              >
                <Spinner v-if="progressSaving" class="mx-auto"/>
                <span v-else>Next</span>
              </button>
            </div>
          </section>
        </div>
      </div>
      <!-- ### -->

      <!-- Info boxes sidebar -->
      <div>
        <info-box class="m-2">
          Build your menu by checking the box next to the items that you would
          like to make available for selection by your attendees and set a
          default for each category. If your attendee does not complete their
          order, the the default will be selected automatically when it comes
          time to build their boxes.
        </info-box>

        <CatererDetailsSidebar @changed="refresh"/>
      </div>
      <!-- ### -->
    </div>
  </div>
</template>

<script>
import CatererDetailsSidebar from '@/components/order/caterer/CatererDetailsSidebar'
import Spinner from '@/components/common/Spinner'
import Api from '@/api/api_v1.0'
import { ORDER_MENU_TYPE } from '@/utils/order-menu-type'
import CustomCheckBox from '@/components/CustomCheckBox'
import CustomRadioButton from '@/components/CustomRadioButton'
import InfoBox from '@/components/common/InfoBox.vue'
import ManualItemAmount from '@/components/order/menu/ManualItemAmount'

export default {
  components: {
    CustomRadioButton,
    CustomCheckBox,
    Spinner,
    InfoBox,
    CatererDetailsSidebar,
    ManualItemAmount
  },
  data () {
    return {
      loading        : false,
      progressLoading: false,
      progressSaving : false,
      categories     : [],
    }
  },
  computed: {
    order () {
      return this.$store.state.currentEditingOrder
    },
    canSubmit () {
      return this.categories.some(
          category =>
              category.items.findIndex(item => item.isSelected) > -1 &&
              !!category.default_item,
      )
    },
    menuExists () {
      return this.categories.length > 0
    },

    manualSelectedCategories () {
      return this.$store.getters['manualSelectedItemsQuantityGroupedByCategory']
    }
  },
  mounted () {
    this.refresh()
  },
  methods: {
    async refresh () {
      await this.loadCategories()
      await this.loadMenuChoices()
      if (this.$store.getters.isAuthenticated) await this.loadManualSelectedItems()
    },
    manualSelected (data) {
      this.categories = this.categories.map(category => {
        if (data['category'] === category.id) {
          category.items = category.items.map(item => {

            if (data['item'] === item.id) {
              return { ...item, click_disabled: true }
            }
            return item
          })
        }

        return category
      })
    },
    removeManualSelected (data) {
      this.categories = this.categories.map(category => {
        if (data['category'] === category.id) {
          category.items = category.items.map(item => {

            if (data['item'] === item.id) {
              return { ...item, click_disabled: false }
            }
            return item
          })
        }
        return category
      })
    },
    async loadManualSelectedItems () {
      this.progressLoading = true
      const response = await Api.getOrderManualSelectedItems(this.order.uuid)
      this.progressLoading = false
      if (response.status === 204) return

      this.$store.commit('SetAllManualSelectedItemsQuantity', response.data)
    },

    async loadCategories () {
      this.progressLoading = true
      var response = await Api.getCategoriesForOrder(
          this.order.uuid,
          ORDER_MENU_TYPE.CATERER,
      )
      this.categories = response
          .filter(category => {
            return category.items.length > 0
          })
          .map(function (category) {
            category.items = category.items.map(item => {
              return { ...item, click_disabled: false }
            })

            return { ...category, isSelected: false }
          })
      this.progressLoading = false
    },

    async loadMenuChoices () {
      this.progressLoading = true
      const response = await Api.getMenuChoicesForOrder(this.order.uuid)

      const selectedItems = response.data
      this.categories = this.categories.map(category => {

        category.items.forEach(categoryItem => {
          if (selectedItems.length === 0) {
            categoryItem.isSelected = false
          } else {
            const selectedItem = selectedItems.find(
                userItem => userItem.item_id === categoryItem.id,
            )
            categoryItem.isSelected = !!selectedItem
            if (selectedItem && selectedItem.is_default) {
              category.default_item = selectedItem.item_id
            }
          }
        })

        return category
      })

      this.progressLoading = false
    },

    onChangeDefaultItem (category) {
      const defaultItem = category.items.find(
          item => item.id === parseInt(category.default_item),
      )
      if (!defaultItem.isSelected) {
        defaultItem.isSelected = true
      }
    },

    onSelectItem (category) {
      const defaultItem = category.items.find(
          item => item.id === parseInt(category.default_item),
      )
      if (!defaultItem || !defaultItem.isSelected) {
        category.default_item =
            category.items.find(item => !!item.isSelected)?.id ?? ''
      }
    },

    async submit () {
      if (this.categories.length === 0) {
        this.$emit('submit')
        return
      }

      this.progressSaving = true

      // save selected menu items
      const parsedCategories = JSON.parse(JSON.stringify(this.categories))
      const filteredCategories = parsedCategories.filter(category => {
        category.items = category.items.filter(item => {
          item.is_default = parseInt(category.default_item) === item.id ? 1 : 0
          return item.isSelected
        })
        return category.items.length > 0
      })

      await Api.saveMenuChoicesForOrder(this.order.uuid, filteredCategories)

      if (this.$store.getters.isAuthenticated) {
        // created suborder
        const response = await Api.createSubOrder(this.order.uuid)
        const suborder = response.data

        // call save manual selected items
        await Api.saveOrderManualSelectedItems(this.order.uuid, suborder.id, { categories: this.manualSelectedCategories })
      }

      this.progressSaving = false
      this.$emit('submit')

    },

    trimString (string, length) {
      return string.length > length
          ? string.substring(0, length - 3) + '...'
          : string
    },
  },
}
</script>
